import { createContext, PropsWithChildren, useCallback, useRef, useState } from 'react';

import { ipToLocation } from 'api/geoLocationService/ipToLocation';
import { MatrixDeviceVerificationProvider } from 'contexts/MatrixDeviceVerificationContext/MatrixDeviceVerificationContext';
import useIsFeatureEnabled from 'hooks/useIsFeatureEnabled';
import { FeatureFlag } from 'utils/Constants';

import type { MatrixDeviceContextValue } from './types';
import useCryptoStateSync from './useCryptoStateSync';
import useHasSecurityCode from './useHasSecurityCode';
import useHasVerifiedDevices from './useHasVerifiedDevices';
import useMatrixDeviceInfo from './useMatrixDeviceInfo';
import useVerificationRequest from './useVerificationRequest';

export const MatrixDeviceContext = createContext<MatrixDeviceContextValue | null>(null);

const MatrixDeviceProvider = ({ children }: PropsWithChildren) => {
  const isCrossSigningEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_CROSS_SIGNING);
  const {
    devicesVerificationStatus,
    checkForVerifiedDevices,
    isLoading: isLoadingVerifiedDevices,
  } = useHasVerifiedDevices();
  const {
    crossSigningStatus,
    refreshCurrentDeviceStatus,
    markDeviceVerified,
    setIsResettingKeys,
    isResettingKeys,
    isAccountRemotelyInvalidated,
  } = useMatrixDeviceInfo();
  const { hasSecurityCode, refreshHasSecurityCode } = useHasSecurityCode();
  const [keyRestoreComplete, setKeyRestoreComplete] = useState(false);
  const {
    activeRequest,
    hasIncomingRequest,
    hasCompletedSelfVerification,
    acceptRequest,
    requestVerification,
    cancelRequest,
  } = useVerificationRequest({ refreshCurrentDeviceStatus, crossSigningStatus });

  // If user tries to verify multiple devices from same ip, we won't fetch again for same ip we will have it here
  const ipLocationCache = useRef<Map<string, string | null>>(new Map());

  useCryptoStateSync({
    refreshCurrentDeviceStatus,
    checkForVerifiedDevices,
    refreshSecurityCodeState: refreshHasSecurityCode,
  });

  const getLocationFromIp = useCallback(async (ip?: string) => {
    if (!ip) return null;
    if (ipLocationCache.current.has(ip)) return ipLocationCache.current.get(ip) ?? null;

    const location = await ipToLocation(ip);
    ipLocationCache.current.set(ip, location);
    return location;
  }, []);

  return (
    <MatrixDeviceContext.Provider
      value={{
        crossSigningStatus,
        refreshCurrentDeviceStatus,
        markDeviceVerified,
        setIsResettingKeys,
        isResettingKeys,
        activeRequest,
        hasIncomingRequest,
        hasCompletedSelfVerification,
        requestVerification,
        acceptRequest,
        cancelRequest,
        devicesVerificationStatus,
        checkForVerifiedDevices,
        isLoadingVerifiedDevices,
        getLocationFromIp,
        hasSecurityCode,
        refreshHasSecurityCode,
        keyRestoreComplete,
        setKeyRestoreComplete,
        isAccountRemotelyInvalidated,
      }}
    >
      {isCrossSigningEnabled ? (
        <MatrixDeviceVerificationProvider>{children}</MatrixDeviceVerificationProvider>
      ) : (
        children
      )}
    </MatrixDeviceContext.Provider>
  );
};

export default MatrixDeviceProvider;
